import { Button, Stack, Typography } from "@mui/material"
import { Frame } from "../../components/layouts/Frame"
import { DataTable } from "../../components/global/Table/DataTable"
import { ActiveIcon } from "../../assets/svgs/ActiveIcon";
import { Link } from "../../components/global/Link";
import { EditIcon } from "../../assets/svgs/EditIcon";
import { DeactiveIcon } from "../../assets/svgs/DeactiveIcon";
import { UsersIcon } from "../../assets/svgs/UsersIcon";
import { Route, Routes, useNavigate } from "react-router-dom";
import { AddUser } from "./AddUser";
import { EditUser } from "./EditUser";
import { DataTableHeadCellData } from "../../components/global/Table/DataTableHeader";
import { useEffect, useRef, useState } from "react";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../assets/contexts/AuthContext";
import { ClientData, UserData } from "../../types";

const superAdminHeadCells: DataTableHeadCellData[] = [
  {
    id: 'name',
    disablePadding: true,
    label: 'Name',
    align: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'client',
    disablePadding: true,
    label: 'Client',
    align: 'left',
    cellAlign: 'left',
    sortable: true,
    searchable: true,
  },
  {
    id: 'active',
    disablePadding: false,
    label: 'Active',
    width: '50px',
  },
  {
    id: 'edit',
    disablePadding: false,
    label: 'Edit',
    width: '50px',
  },
];

const clientAdminHeadCells: DataTableHeadCellData[] = [
  superAdminHeadCells[0], 
  superAdminHeadCells[2], 
  superAdminHeadCells[3]
];

interface UserTableData {
  id: any;
  name: any;
  client: any;
  active: JSX.Element;
  edit: JSX.Element;
}

export const Users = () => {
  const navigate = useNavigate();
  const isLoading = useRef(false);
  const [sIsLoading, setSIsLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { fetchData } = useAPI();
  const [rows, setRows] = useState<UserTableData[]>([]);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [users, setUsers] = useState<UserData[]>([]);
  const [headCells, setHeaderCells] = useState<DataTableHeadCellData[]>([]);

  const refetchUsers = async () => {
    setSIsLoading(true);
    isLoading.current = true;
    const {errors, data} = await fetchData('/users/all');
    setUsers(data ?? []);
    isLoading.current = false;
    setSIsLoading(false);
  }

  const refetchClients = async () => {
    const {errors, data} = await fetchData('/clients/all');
    setClients(data ?? []);
  }

  const onAddUser = () => {
    navigate('/users/create');
  }

  const onBulkDelete = async (ids: number[]) => {
    await fetchData('/users/delete',  { id: ids }, 'POST');
    refetchUsers();
  }

  useEffect(() => {
    if (isAuthenticated && !isLoading.current) {
      refetchUsers();
      refetchClients();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user?.is_super_admin) {
      setHeaderCells(superAdminHeadCells);
    } else {
      setHeaderCells(clientAdminHeadCells);
    }
  }, [user]);

  useEffect(() => {
    setRows(getRows(users));
  }, [users, clients]);

  const getRows = (users: UserData[]) => {
    const result: UserTableData[] = [];
    for (let i=0; i<users.length; i++) {
      const client = clients.find((item) => item.id == users[i].client_id);
      result.push({
        id: `${users[i].id}`,
        name: <Typography sx={{color: users[i].status ? "inherit" : "#DCDCE0"}} >{users[i].first_name} {users[i].last_name}</Typography>,
        client: <Typography>{client?.name ?? ''}</Typography>,
        active: users[i].status ? <ActiveIcon /> : <DeactiveIcon />,
        edit: <Link to={`/users/${users[i].id}`}><EditIcon /></Link>
      });
    }
    return result;
  }

  return (
    <Frame title="Users" toolbarButtons={(
      <>
        <Button variant="contained" onClick={onAddUser} startIcon={<UsersIcon width="22" height="15" color="#FFFFFF" />}>
          Add User
        </Button>
      </>
    )}>
      <Stack>
        <Routes>
          <Route
            path="/create/"
            element={<AddUser refetchUsers={refetchUsers} clients={clients} />}
          ></Route>
          <Route
            path="/:user_id/"
            element={<EditUser refetchUsers={refetchUsers} clients={clients} />}
          ></Route>
        </Routes>
        <DataTable 
          title="Users" 
          hideDeleteButton={true}
          hideIdCheckbox={true}
          headCells={headCells} 
          rows={rows} 
          onBulkDelete={onBulkDelete} 
          isLoading={sIsLoading} 
        />
      </Stack>
    </Frame>
  )
}
